// Modal para crear o modificar un usuario
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTexts, TXT } from '../../context/TextContext';
import { usersApi } from '../../services/api';
import CustomSelect from '../CustomSelect';

const TIMEZONES = [
  { label: 'UTC',                                     value: 'UTC' },
  { label: 'America/Argentina/Buenos_Aires (ART -3)', value: 'America/Argentina/Buenos_Aires' },
  { label: 'America/Montevideo (UYT -3)',              value: 'America/Montevideo' },
  { label: 'America/Santiago (CLT -4/-3)',             value: 'America/Santiago' },
  { label: 'America/Bogota (COT -5)',                  value: 'America/Bogota' },
  { label: 'America/Lima (PET -5)',                    value: 'America/Lima' },
  { label: 'America/Mexico_City (CST -6/-5)',          value: 'America/Mexico_City' },
  { label: 'America/Chicago (CST -6/-5)',              value: 'America/Chicago' },
  { label: 'America/New_York (EST -5/-4)',             value: 'America/New_York' },
  { label: 'America/Denver (MST -7/-6)',               value: 'America/Denver' },
  { label: 'America/Los_Angeles (PST -8/-7)',          value: 'America/Los_Angeles' },
  { label: 'America/Sao_Paulo (BRT -3/-2)',            value: 'America/Sao_Paulo' },
  { label: 'Europe/London (GMT 0/+1)',                 value: 'Europe/London' },
  { label: 'Europe/Madrid (CET +1/+2)',                value: 'Europe/Madrid' },
  { label: 'Europe/Paris (CET +1/+2)',                 value: 'Europe/Paris' },
  { label: 'Europe/Berlin (CET +1/+2)',                value: 'Europe/Berlin' },
  { label: 'Asia/Dubai (GST +4)',                      value: 'Asia/Dubai' },
  { label: 'Asia/Kolkata (IST +5:30)',                 value: 'Asia/Kolkata' },
  { label: 'Asia/Tokyo (JST +9)',                      value: 'Asia/Tokyo' },
  { label: 'Australia/Sydney (AEST +10/+11)',          value: 'Australia/Sydney' },
];

const BLANK = {
  login: '', apeyNom: '', smtp: '', cuil: '',
  idioma: 1, timezone: 'UTC', grpId: '', perId: '', habilitado: true,
};

export default function UserModal({ show, userData, groups, profiles, onClose, onSaved }) {
  const { user } = useAuth();
  const { t } = useTexts();

  const [form, setForm]   = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const isNew = !userData?.Usr_IdUsr;

  useEffect(() => {
    if (!show) return;
    if (userData) {
      const enabled = userData.Usr_Habilitado === 'Y' || userData.Usr_Habilitado === 'S';
      setForm({
        login:      userData.Usr_Login    || '',
        apeyNom:    userData.Usr_ApeyNom  || '',
        smtp:       userData.Usr_SMTP     || '',
        cuil:       userData.Usr_Cuil     || '',
        idioma:     userData.Usr_idioma   || user?.idioma || 1,
        timezone:   userData.Usr_Timezone || user?.timezone || 'UTC',
        grpId:      userData.Grp_IdGrp    || (groups[0]?.Grp_IdGrp ?? ''),
        perId:      userData.Per_IdPer    || '',
        habilitado: enabled,
      });
    } else {
      setForm({
        ...BLANK,
        idioma:   user?.idioma   || 1,
        timezone: user?.timezone || 'UTC',
        grpId:    groups[0]?.Grp_IdGrp ?? '',
      });
    }
    setError('');
  }, [show, userData, groups, user]);

  if (!show) return null;

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.login.trim() || !form.apeyNom.trim()) {
      setError('Login y Apellido y Nombre son requeridos');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        login:      form.login.trim(),
        apeyNom:    form.apeyNom.trim(),
        smtp:       form.smtp.trim(),
        cuil:       form.cuil.trim(),
        idioma:     form.idioma,
        timezone:   form.timezone,
        grpId:      form.grpId || null,
        perId:      form.perId || null,
        habilitado: form.habilitado,
      };
      if (isNew) {
        const res = await usersApi.create(payload);
        onSaved({ isNew: true, clave: res.data?.clave });
      } else {
        await usersApi.update(userData.Usr_IdUsr, payload);
        onSaved({ isNew: false, id: userData.Usr_IdUsr, form });
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = { marginBottom: '0.75rem' };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, marginBottom: 2, display: 'block' };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 540 }}>
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">
              {isNew ? t(TXT.NEW, 'Nuevo') + ' ' + t(TXT.USUARIO_LABEL, 'usuario')
                     : t(TXT.UPDATE, 'Modificar') + ' ' + t(TXT.USUARIO_LABEL, 'usuario')}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {error && <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>{error}</div>}

            <div style={fieldStyle}>
              <label style={labelStyle}>{t(TXT.USUARIO_LABEL, 'Usuario')}</label>
              <input className="form-control form-control-sm" value={form.login}
                onChange={e => set('login', e.target.value)}
                disabled={!isNew}
                autoFocus={isNew}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t(TXT.APELLIDO_NOMBRE, 'Apellido y Nombre')}</label>
              <input className="form-control form-control-sm" value={form.apeyNom}
                onChange={e => set('apeyNom', e.target.value)}
                autoFocus={!isNew}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t(TXT.MAIL_LABEL, 'Mail')}</label>
              <input className="form-control form-control-sm" type="email" value={form.smtp}
                onChange={e => set('smtp', e.target.value)}
              />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t(TXT.CUIL, 'CUIL')}</label>
              <input className="form-control form-control-sm" value={form.cuil}
                onChange={e => set('cuil', e.target.value)}
              />
            </div>

            <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="chk_habilitado" className="form-check-input"
                checked={form.habilitado}
                onChange={e => set('habilitado', e.target.checked)}
                style={{ marginTop: 0 }}
              />
              <label htmlFor="chk_habilitado" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                Habilitado
              </label>
            </div>

            <div className="row" style={{ marginBottom: '0.75rem' }}>
              <div className="col-6">
                <label style={labelStyle}>{t(TXT.IDIOMA_LABEL, 'Idioma')}</label>
                <CustomSelect value={form.idioma} onChange={e => set('idioma', parseInt(e.target.value))}>
                  <option value={2}>Español</option>
                  <option value={1}>English</option>
                </CustomSelect>
              </div>
              <div className="col-6">
                <label style={labelStyle}>{t(TXT.TIMEZONE, 'Huso horario')}</label>
                <CustomSelect value={form.timezone} onChange={e => set('timezone', e.target.value)}>
                  {TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </CustomSelect>
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>{t(TXT.GRUPO_USUARIOS, 'Grupo de usuarios')}</label>
              <CustomSelect value={form.grpId} onChange={e => set('grpId', e.target.value)}>
                {groups.map(g => (
                  <option key={g.Grp_IdGrp} value={g.Grp_IdGrp}>{g.Grp_DesGrp}</option>
                ))}
              </CustomSelect>
            </div>

            <div style={{ ...fieldStyle, marginBottom: 0 }}>
              <label style={labelStyle}>
                {t(TXT.PERFIL_FUNCIONES, 'Perfil de funciones')}{' '}
                <span style={{ fontWeight: 400, color: '#888' }}>(opcional)</span>
              </label>
              <CustomSelect value={form.perId} onChange={e => set('perId', e.target.value)}>
                <option value="">— {t(TXT.SIN_PERFIL, 'Sin perfil')} —</option>
                {profiles.map(p => (
                  <option key={p.Per_IdPer} value={p.Per_IdPer}>{p.Per_DesPer}</option>
                ))}
              </CustomSelect>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="button" onClick={onClose} disabled={saving}>
              {t(TXT.CANCEL, 'Cancelar')}
            </button>
            <button type="button" className="button color1" onClick={handleSave} disabled={saving}>
              {saving
                ? <span className="spinner-border spinner-border-sm" />
                : t(TXT.SAVE, 'Guardar')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
